import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VoiceAssistantTrigger from './VoiceAssistantTrigger';
import './FloatingActionBar.css';

interface VoiceResult {
  success: boolean;
  message: string;
  transaction_id?: number;
  data?: any;
}

interface FloatingActionBarProps {
  onVoiceResult?: (result: VoiceResult) => void;
  onImageUpload?: () => void;
  onHistory?: () => void;
  onCloudUpload?: () => void;
  onVoiceAssistantOpen?: () => void;
  isVoiceRecording?: boolean;
  hideVoiceTrigger?: boolean;
}

const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  onImageUpload,
  onHistory,
  onCloudUpload,
  onVoiceAssistantOpen,
  isVoiceRecording = false,
  hideVoiceTrigger = false,
}) => {
  const { t } = useTranslation();

  const handleImageUpload = () => {
    if (onImageUpload) {
      onImageUpload();
    } else {
      window.location.href = '/image-processing';
    }
  };

  const handleHistory = () => {
    if (onHistory) {
      onHistory();
    } else {
      window.location.href = '/journal-entries';
    }
  };

  const handleCloudUpload = () => {
    if (onCloudUpload) {
      onCloudUpload();
    } else {
      window.location.href = '/integrations';
    }
  };

  // CRITICAL: Ensure microphone launcher stays visible above page content.
  useEffect(() => {
    const ensureVisibility = () => {
      const fabFooter = document.querySelector('.modern-fab-footer') as HTMLElement;
      const voiceTrigger = document.querySelector('.enhanced-voice-trigger') as HTMLElement;

      if (fabFooter) {
        fabFooter.style.display = 'block';
        fabFooter.style.visibility = 'visible';
        fabFooter.style.opacity = '1';
        fabFooter.style.position = 'fixed';
        fabFooter.style.bottom = '0';
        fabFooter.style.left = '0';
        fabFooter.style.width = '100%';
        fabFooter.style.zIndex = '1000';
      }

      if (voiceTrigger) {
        voiceTrigger.style.display = 'flex';
        voiceTrigger.style.visibility = 'visible';
        voiceTrigger.style.opacity = '1';
        voiceTrigger.style.zIndex = '1001';
      }
    };

    ensureVisibility();
    const interval = setInterval(ensureVisibility, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <footer
      className="modern-fab-footer"
      style={{
        display: 'block',
        visibility: 'visible',
        opacity: 1,
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        zIndex: 1000
      }}
    >
      <div className="modern-fab-container">
        <div className="modern-fab-center">
          {!hideVoiceTrigger && (
            <VoiceAssistantTrigger
              onClick={onVoiceAssistantOpen || (() => {})}
              isRecording={isVoiceRecording}
            />
          )}
        </div>

        <div className="modern-fab-actions">
          <button
            className="modern-fab-action-btn"
            onClick={() => window.location.href = '/voice-assistant'}
            title={t('voice.modal.voiceAssistant')}
          >
            <span className="material-icons">apps</span>
          </button>

          <button
            className="modern-fab-action-btn"
            onClick={handleImageUpload}
            title={t('voice.modal.imageProcessing')}
          >
            <span className="material-icons">image</span>
          </button>

          <div className="modern-fab-spacer"></div>

          <button
            className="modern-fab-action-btn"
            onClick={handleHistory}
            title={t('voice.modal.transactionHistory')}
          >
            <span className="material-icons">history</span>
          </button>

          <button
            className="modern-fab-action-btn"
            onClick={handleCloudUpload}
            title={t('voice.modal.cloudUpload')}
          >
            <span className="material-icons">cloud_upload</span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default FloatingActionBar;
